import React, { useState, useEffect } from "react";
import NavBar from "../../components/NavBar/NavBar";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { WithContext as ReactTags } from "react-tag-input";
import Swal from "sweetalert2";
import Modal from "react-modal"; 
import {
  getParentListAPI,
  upsertPromocode,
  sendPromocodeEmail,
  SchoolListAPI,
  MasterSchoolBoardListAPI,
  CityListAPI,
} from "../../api/apiHandler";
import { discountTypeOption } from "../Common/CommonFunctions";
import moment from "moment";
import Heading from "../../components/Heading/Heading";

const schema = yup.object().shape({
  name: yup.string().required("Please enter name"),
  unique_code: yup.string().required("Please enter unique code"),
  promotion_sponsor_code: yup
    .string()
    .required("Please enter promotion sponsor code"),
  usage_limit: yup.string().required("Please enter usage limit"),
  discount_type: yup.string().required("Please enter discount type"),
  max_discount_amount: yup.number().when("discount_type", {
    is: "PERCENTAGE" || "FREE_MEAL",
    then: (schema) =>
      schema
      .required("Please enter max discount amount")
      .min(0, "Must be more than 0"),
    otherwise: (schema) => schema.notRequired(),
  }),
  discount_value: yup.number().required().min(0, "Must be more than 0"),
  minimum_order_value: yup.number().required().min(0, "Must be more than 0"),
  description: yup.string().required("Please enter description"),
  valid_upto: yup.array().typeError("Please select dates"),
  tags: yup
    .array()
    .typeError("Please select at least one.")
    .when("school_or_parent", {
      is: (val) => val !== "ALL",
      then: (schema) => schema.min(1, "Please select at least one."),
      otherwise: (schema) => schema.notRequired(),
    }),
  subject : yup.string().required("please enter subject"),
  message : yup.string().required("please enter message"),
});

function AddPromocode() {
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    getValues,
    watch,
    trigger,
  } = useForm({
    defaultValues: {
      tags: [],
      school_or_parent: "ALL",
    },
    resolver: yupResolver(schema),
  });

  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState([null, null]);
  const [first, setFirst] = useState(0);
  const [validFrom, validTo] = dateRange;
  const [parents, setParents] = useState([]);
  const [schools, setSchools] = useState([]);
  const [city, setCity] = useState([]);
  const [masterSchoolBoards, setMasterSchoolBoards] = useState([]);
  const [discountType, setDiscountType] = useState(null);
  const [schoolOrParent, setSchoolOrParent] = useState("ALL");
  const [label, setLabel] = useState("Discount value");
  const [walletModal, setWalletModal] = useState(false);
  const walletOptions = [
  { label: "Donor Donation Wallet", value: "donor_donation_wallet" },
  ];
  const [selectedWallet, setSelectedWallet] = useState(walletOptions[0]);
  const [totalAmount, setTotalAmount] = useState(0);
  const donor_id = localStorage.getItem("DAid");

  const total_amount_calculate = () => {
    const discountType = getValues("discount_type");
    const discountValue = Number(getValues("discount_value")) || 0;
    const maxDiscountAmount = Number(getValues("max_discount_amount")) || 0;
    const usageLimit = Number(getValues("usage_limit")) || 0;

    let total = 0;

    if (discountType === "FLAT_AMOUNT") {
      total = discountValue * usageLimit;
    } else if (discountType === "PERCENTAGE") {
      total = maxDiscountAmount * usageLimit;
    }else if (discountType === "FREE_MEAL"){
      total = maxDiscountAmount * usageLimit;
    }

    setTotalAmount(total);
  };
  const watchDiscountType = watch("discount_type");
  const watchDiscountValue = watch("discount_value");
  const watchMaxDiscountAmount = watch("max_discount_amount");
  const watchUsageLimit = watch("usage_limit");
  useEffect(()=>{
    fetchParents();
    fetchSchools();
    fetchSchoolBoards();
    fetchCity();
  },[]);

  useEffect(() => {
    total_amount_calculate();
  }, [watchDiscountType, watchDiscountValue, watchMaxDiscountAmount, watchUsageLimit]);


  const formatLabel = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .replace(/_/g, " ") 
    .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const fetchParents = async () => {
    try {
      let pageInfo = {
        limit : 5000
      };
      const response = await getParentListAPI(pageInfo);

      const parentData = response?.data?.data?.data || [];
      setParents(parentData);
    } catch (error) {
      console.error("Error fetching parents:", error);
      setParents([]); 
    }
  };
  const fetchSchools = async () => {
    try {
      const response = await SchoolListAPI();
      const schoolData = response?.data?.data || [];
      setSchools(schoolData);
    } catch (error) {
      console.error("Error fetching schools:", error);
      setSchools([]); 
    }
  };
  const fetchSchoolBoards = async () => {
    try{
    let pageInfo = {
      limit: 5000,
    };
    const response = await MasterSchoolBoardListAPI(pageInfo);
    if (response.data.code === "1") {
      setMasterSchoolBoards(response.data.data.data);
    }
    }catch (e){
      console.error("Error fetching schoolBoard:", e);
      setMasterSchoolBoards([]);
    }
  };

  const fetchCity = async () => {
    try{
    let pageInfo = {
      allActiveItems : true
    };
    const response = await CityListAPI(pageInfo);
    if (response.data.code === "1") {
      setCity(response.data.data.data);
    }
    }catch (e){
      console.error("Error fetching schoolBoard:", e);
      setCity([]);
    }
  };

  const parentSuggestions = parents.map((p) => ({
    id: String(p?.id || ""),
    text: p?.email || "Unknown Parent",
  }));
  const boardSuggestions = masterSchoolBoards.map((b) => ({
    id: (b.school_board ? b.school_board.trim() : "" ), 
    text: (b.school_board || "Unknown School Board"),
    schools: (() => {
      try {
        return JSON.parse(b?.schools || "[]");
      } catch (e) {
        return [];
      }
    })(),
  }));
  console.log("boardchgjh", boardSuggestions);

  const schoolSuggestions = schools.map((s) => ({
    id: String(s?.master_school_id || ""),
    text: s?.school_name || "Unknown School",
    email: s?.email || "",
    school_id: s?.id || "",
  }));
  console.log("sfuyjgs",schoolSuggestions)

  const citySuggetions = city.map((c) => ({
    id: String(c?.id || ""),
    text: c?.city_name || "Unknown City",
    schools : c?.schools || [],
  }));

  const suggestions =
    schoolOrParent === "PARENT"
      ? parentSuggestions
      : schoolOrParent === "SCHOOL"
      ? schoolSuggestions
      : schoolOrParent === "SCHOOL_BOARD"
      ? boardSuggestions
      : schoolOrParent === "CITY"
      ? citySuggetions
      : [];

  const handleDelete = (index) => {
    const updatedTags = tags.filter((_, i) => i !== index);
    syncTagsWithForm(updatedTags);
  };

  const handleAddition = (tag) => {
    const updatedTags = [...tags, tag];
    syncTagsWithForm(updatedTags);
  };

  const syncTagsWithForm = (updatedTags) => {
    setTags(updatedTags);
    setValue("tags", [...updatedTags], { shouldValidate: true });
  };

  const handleDrag = (tag, currPos, newPos) => {
    const newTags = tags.slice();
    newTags.splice(currPos, 1);
    newTags.splice(newPos, 0, tag);
    setTags(newTags);
  };

  const onClearAll = () => setTags([]);

  const handleLabel = (type) => {
    let text = "";
    if (type === "FREE_MEAL") {
      text = "No of free meal";
    } else if (type === "PERCENTAGE") {
      text = "Percentage";
    } else {
      text = "Discount value";
    }
    setLabel(text);
  };

  const handleOnRadioChange = (type) => {
    onClearAll();
    setSchoolOrParent(type);
  };

  const handleWalletSubmit = async () => {
    try {
      const isValid = await trigger(["subject", "message"]);

      if (!isValid) {
        return;
      }
      if (totalAmount <= 0) {
        setWalletModal(false);
        Swal.fire("Error", "Please enter a valid total amount.", "error");
        return;
      }
      setWalletModal(false);
      const formData = getValues();
      const submitData = {
        ...formData,
        wallet_type: selectedWallet.value,
        total_amount: totalAmount,
      };
      const response = await onSubmit(submitData);
      if (response && response.data.code === "1") {
        let recipient_emails = [];
        if (schoolOrParent === "PARENT") {
          recipient_emails = tags.map((tag) => tag.text)
        } else if (schoolOrParent === "SCHOOL") {
          recipient_emails = tags.map((tag) => tag?.email || "")
        } else if(schoolOrParent === "SCHOOL_BOARD"){
          recipient_emails =tags
            .flatMap((tag) => tag?.schools?.map((s) => s.email) || [])
            .filter(Boolean)
            .join(", ")
        } else if(schoolOrParent === "CITY"){
          recipient_emails = tags
            .flatMap((tag) => tag?.schools || [])
            .filter(Boolean)
            .join(", ")
        } else {
          recipient_emails = schools.map((s) => s?.email).filter(Boolean)
        }
        if (recipient_emails.length > 0) {
          const emailData = {
            recipient_email: recipient_emails,
            subject:   formData.subject || "New Promotion Code Available ",
            message: formData.message,
            wallet_type: selectedWallet.label,
            max_amount: formData.discount_value,
            promo_code_name: formData.name,
            promo_unique_code : formData.unique_code,
            type_of_promotion: formData.description,
          };
          await sendPromocodeEmail(emailData);
        }
        reset();
        onClearAll();
        Swal.fire("Food For Good", response.data.message, "success").then(
          (result) => {
            navigate("/manage-promocode");
          }
        );
      } else {
        Swal.fire("Food For Good", response.data.message, "error");
      }
    } catch (error) {
      setWalletModal(false);
      Swal.fire(
        "Error",
        "Something went wrong while processing your request. Please try again.",
        "error"
      );
    }
  };

  const onSubmit = async (submitData) => {
    const selectedIds =
      schoolOrParent === "SCHOOL_BOARD"
      ? tags.map((t) => t.text) 
      : tags.map((t) => parseInt(t.id));
    let req = {
      donor_id: donor_id,
      parent: schoolOrParent === "PARENT" ? selectedIds : [],
      school: schoolOrParent === "SCHOOL" ? selectedIds : [],
      school_board: schoolOrParent === "SCHOOL_BOARD" ? selectedIds : [],
      city : schoolOrParent === "CITY" ? selectedIds : [],
      valid_from: moment(validFrom).format("YYYY-MM-DD"),
      valid_to: moment(validTo).format("YYYY-MM-DD"),
      promo_code_type: schoolOrParent,
      create_by_type: "DONOR",
    };
    let requestData = { ...submitData, ...req };
    const response = await upsertPromocode(requestData);
    return response;
  };

  return (
    <>
      <NavBar />
      <section className="content home ">
        <Heading title="Add Promocode" />
        <div className="container-fluid">
          <div className="row clearfix">
            <div className="col-lg-12">
              <div className="card">
                <div className="body">
                  <div className="row">
                    <div className="preview col-md-12">
                      <div className="preview-pic tab-content">
                        <div className="tab-pane active" id="product_1"></div>
                      </div>
                    </div>
                    <div className="details col-lg-8 col-md-12">
                      <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="col-sm-12 pl-3">
                          <div className="form-group">
                            <div className="form-line">
                              <p style={{ marginBottom: "0px" }}>Name</p>
                              <input
                                type="text"
                                className="form-control"
                                name="name"
                                {...register("name")}
                              />
                            </div>
                            <p className="mt-2 font-bold col-red">
                              {errors.name?.message}
                            </p>
                          </div>

                          <div className="form-group">
                            <div className="form-line">
                              <p style={{ marginBottom: "0px" }}>
                                Unique code
                              </p>
                              <input
                                type="text"
                                className="form-control"
                                name="unique_code"
                                {...register("unique_code")}
                              />
                            </div>
                            <p className="mt-2 font-bold col-red">
                              {errors.unique_code?.message}
                            </p>
                          </div>

                          <div className="form-group">
                            <div className="form-line">
                              <p style={{ marginBottom: "0px" }}>
                                Discount type
                              </p>
                              <Select
                                options={discountTypeOption}
                                placeholder={"Discount type"}
                                onChange={(e) => {
                                  setDiscountType(e);
                                  setValue("discount_type", e?.value, {
                                    shouldValidate: true,
                                  });
                                  handleLabel(e?.value);
                                }}
                                className="form-control"
                                value={discountType}
                                isClearable
                              />
                              <input
                                type="hidden"
                                className="form-control"
                                name="discount_type"
                                {...register("discount_type")}
                              />
                            </div>
                            <p className="mt-2 font-bold col-red">
                              {errors.discount_type?.message}
                            </p>
                          </div>
                          <div className="form-group">
                            <div className="form-line">
                              <p style={{ marginBottom: "0px" }}>{label}</p>
                              <input
                                type="number"
                                className="form-control"
                                name="discount_value"
                                {...register("discount_value")}
                                defaultValue={0}
                                min={1}
                              />
                            </div>
                            <p className="mt-2 font-bold col-red">
                              {errors.discount_value?.message}
                            </p>
                          </div>
                          {(discountType?.value === "PERCENTAGE" ||
                            discountType?.value === "FREE_MEAL") && (
                            <div className="form-group">
                              <div className="form-line">
                                <p style={{ marginBottom: "0px" }}>
                                  Max discount amount
                                </p>
                                <input
                                  type="number"
                                  className="form-control"
                                  name="max_discount_amount"
                                  {...register("max_discount_amount")}
                                  min={1}
                                />
                              </div>
                              <p className="mt-2 font-bold col-red">
                                {errors.max_discount_amount?.message}
                              </p>
                            </div>
                          )}

                          <div className="form-group">
                            <div className="form-line">
                              <p style={{ marginBottom: "0px" }}>
                                Promotion sponsor code
                              </p>
                              <input
                                type="text"
                                className="form-control"
                                name="promotion_sponsor_code"
                                {...register("promotion_sponsor_code")}
                              />
                            </div>
                            <p className="mt-2 font-bold col-red">
                              {errors.promotion_sponsor_code?.message}
                            </p>
                          </div>

                          <div className="form-group">
                            <div className="form-line">
                              <p style={{ marginBottom: "0px" }}>
                                Usage Limit
                              </p>
                              <input
                                type="number"
                                className="form-control"
                                name="usage_limit"
                                {...register("usage_limit")}
                                min={0}
                              />
                            </div>
                            <p className="mt-2 font-bold col-red">
                              {errors.usage_limit?.message}
                            </p>
                          </div>

                          <div className="form-group">
                            <div className="form-line">
                              <p style={{ marginBottom: "0px" }}>
                                Valid Upto
                              </p>
                              <DatePicker
                                dateFormat="dd-MM-yyyy"
                                selectsRange={true}
                                startDate={validFrom}
                                endDate={validTo}
                                onChange={(update) => {
                                  setDateRange(update);
                                  setValue("valid_upto", update, {
                                    shouldValidate: true,
                                  });
                                  setPage(1);
                                  setFirst(0);
                                }}
                                placeholderText="Select Date"
                                className="form-control"
                                isClearable={true}
                              />
                            </div>
                            <input
                              type="hidden"
                              className="form-control"
                              name="valid_upto"
                              {...register("valid_upto")}
                            />
                            <p className="mt-2 font-bold col-red">
                              {errors.valid_upto?.message}
                            </p>
                          </div>

                          <div className="form-group">
                            <p>Allow multiple times</p>
                            <input
                              type="checkbox"
                              id="checkbox_penuts"
                              className="filled-in"
                              {...register("allow_multiple_uses")}
                            />
                            <label
                              for="checkbox_penuts"
                              className="mr-1"
                            ></label>
                            <p className="mt-2 font-bold col-red">
                              {errors.allow_multi_time?.message}
                            </p>
                          </div>
                          <div className="form-group">
                            <h4 style={{ marginBottom: "8px" }}>
                              Select Promocode Type
                            </h4>
                            <input
                              type="radio"
                              value="ALL"
                              id="school_all"
                              {...register("school_or_parent", {
                                onChange: (e) =>
                                  handleOnRadioChange(e.target.value),
                              })}
                              defaultChecked
                            />
                            <label className="mr-2" htmlFor="school_all">All</label>
                            <input
                              type="radio"
                              value="SCHOOL_BOARD"
                              id="school_board"
                              {...register("school_or_parent", {
                                onChange: (e) =>
                                  handleOnRadioChange(e.target.value),
                              })}
                            />
                            <label className="mx-2" htmlFor="school_board">School Board</label>
                            <input
                              type="radio"
                              value="CITY"
                              id="school_city"
                              {...register("school_or_parent", {
                                onChange: (e) =>
                                  handleOnRadioChange(e.target.value),
                              })}
                            />
                            <label className="mx-2" htmlFor="school_city">City</label>
                            <input
                              type="radio"
                              value="SCHOOL"
                              id="school_school"
                              {...register("school_or_parent", {
                                onChange: (e) =>
                                  handleOnRadioChange(e.target.value),
                              })}
                            />
                            <label className="mx-2" htmlFor="school_school">School</label>
                            <input
                              type="radio"
                              value="PARENT"
                              id="school_parent"
                              {...register("school_or_parent", {
                                onChange: (e) =>
                                  handleOnRadioChange(e.target.value),
                              })}
                            />
                            <label className="mx-2" htmlFor="school_parent">Parent</label>
                          </div>
                          {(schoolOrParent !== "ALL")&& (
                            <>
                              <div className="form-group">
                                <div className="form-line">
                                  <h4 style={{ marginBottom: "8px" }}>
                                    Add {formatLabel(schoolOrParent)}
                                  </h4>
                                  <div style={{ position: "relative" }}>
                                    <ReactTags
                                      tags={tags}
                                      suggestions={suggestions}
                                      handleDelete={handleDelete}
                                      handleAddition={handleAddition}
                                      handleDrag={handleDrag}
                                      inputFieldPosition="bottom"
                                      placeholder={`Select to add ${formatLabel(
                                        schoolOrParent
                                      )}`}
                                      classNames={{
                                        tagInput: "form-control tag-input",
                                        tag: "tag-item",
                                        tagInputField: "tag-input-field",
                                        selected: "selected-tags",
                                        suggestions: "tag-suggestions",
                                        remove:
                                          "react-tags__remove ReactTags__remove ml-2 cursor-pointer text-red-500 hover:text-red-700",
                                      }}
                                      editable
                                      clearAll
                                      onClearAll={onClearAll}
                                    />
                                    <input
                                      type="hidden"
                                      className="form-control"
                                      name="tags"
                                      {...register("tags")}
                                    />
                                  </div>
                                </div>
                                <p className="mt-2 font-bold col-red">
                                  {errors?.tags?.message}
                                </p>
                              </div>
                            </>
                          )}
                          <div className="form-group">
                            <div className="form-line">
                              <p
                                style={{
                                  marginBottom: "0px",
                                  marginTop: "50px",
                                }}
                              >
                                Minimum Order value
                              </p>
                              <input
                                type="number"
                                className="form-control"
                                name="minimum_order_value"
                                {...register("minimum_order_value")}
                                defaultValue={0}
                                min={0}
                              />
                            </div>
                            <p className="mt-2 font-bold col-red">
                              {errors.minimum_order_value?.message}
                            </p>
                          </div>
                          <div className="form-group">
                            <div className="form-line">
                              <p style={{ marginBottom: "0px" }}>Promotion Description</p>
                              <input
                                type="text"
                                className="form-control"
                                name="description"
                                {...register("description")}
                              />
                            </div>
                            <p className="mt-2 font-bold col-red">
                              {errors.description?.message}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="btn btn-raised btn-primary m-t-15 m-b-15 waves-effect"
                            onClick={async () => {
                              const isValid = await trigger([
                                "name",
                                "unique_code",
                                "promotion_sponsor_code",
                                "usage_limit",
                                "discount_type",
                                "discount_value",
                                "minimum_order_value",
                                "description",
                                "valid_upto",
                                "tags"
                              ]);
                              if (isValid) {
                                setWalletModal(true);
                              } else {
                                setWalletModal(false);
                              }
                            }}
                          >
                          Add Promocode
                          </button>
                        </div>
                      </form>
                      <hr />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Modal
        isOpen={walletModal}
        onRequestClose={() => setWalletModal(false)}
        style={{
          content: {
            width: "700px",
            maxWidth: "90%",
            height: "auto",
            margin: "auto",
            borderRadius: "16px",
            padding: "30px 40px",
            border: "none",
            boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
            backgroundColor: "#fff",
          },
          overlay: { backgroundColor: "rgba(0,0,0,0.6)" },
        }}
      >
        <div className="modal-header border-0 text-center  mb-2">
          <h4 className="fw-bold text-dark m-0"> Promocode Details</h4>
        </div>

        <div className="modal-body px-2">
          <div className="form-group mb-2">
              <label className="form-label fw-semibold">Recipient’s Email Address</label>
              <input
                type="text"
                className="form-control shadow-sm"
                readOnly
                style={{ background: "#f8f9fa" }}
                value={
                  schoolOrParent === "PARENT"
                    ? tags.map((t) => t.text).join(", ")
                    : schoolOrParent === "SCHOOL"
                    ? tags.map((t) => t.email).join(", ")
                    : schoolOrParent === "SCHOOL_BOARD"
                    ? tags
                      .flatMap((tag) => tag?.schools?.map((s) => s.email) || [])
                      .filter(Boolean)
                      .join(", ")
                    : schoolOrParent === "CITY"
                    ? tags
                      .flatMap((tag) => tag?.schools || [])
                      .filter(Boolean)
                      .join(", ")
                    : schools.map((s) => s.email).filter(Boolean).join(", ")
                }
              />
          </div>

          <div className="form-group mb-2">
              <label className="form-label fw-semibold">Subject</label>
              <input
                type="text"
                className="form-control shadow-sm"
                placeholder="Enter email subject"
                {...register("subject")}
                defaultValue="New Promotion Code Available at Your School"
              />
              <p className="mt-2 font-bold col-red">
                {errors.subject?.message}
              </p>
          </div>

          <div className="form-group mb-2">
              <label className="form-label fw-semibold">Message</label>
              <textarea
                className="form-control shadow-sm"
                rows={4}
                placeholder="Enter your message here..."
                {...register("message")}
                defaultValue="You’ve received a new promotion from your school. Use your promo code to enjoy benefits!"
              ></textarea>
             <p className="mt-2 font-bold col-red">
                {errors.message?.message}
              </p>
          </div>

          <hr className="my-2" />

          <div className="row g-2">
            <div className="col-md-6">
              <div className="p-3 bg-light rounded shadow-sm h-100">
                <strong>Promocode :</strong>
                <p className="mb-0 text-secondary">{getValues("unique_code")}</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="p-3 bg-light rounded shadow-sm h-100">
                <strong>Max Amount:</strong>
                <p className="mb-0 text-success fw-semibold">${getValues("discount_value")}</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="p-3 bg-light rounded shadow-sm h-100">
                <strong>Promo Code Name:</strong>
                <p className="mb-0 text-primary">{getValues("name")}</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="p-3 bg-light rounded shadow-sm h-100">
                <strong>Type of Promotion:</strong>
                <p className="mb-0 text-secondary">{getValues("description")}</p>
              </div>
            </div>
          </div>

          <div className="form-group mt-2">
            <label className="form-label fw-semibold">Wallet Account Type</label>
            <Select
              options={walletOptions}
              value={selectedWallet}
              placeholder="Select Wallet Type"
              classNamePrefix="react-select"
              isDisabled={true}
            />
          </div>

          <div className="form-group mt-2">
            <label className="form-label fw-semibold">Total Amount</label>
            <p className="m-0 fs-5 text-success fw-bold">${totalAmount?.toFixed(2)}</p>
          </div>
        </div>

        <div className="modal-footer d-flex justify-content-center gap-3 border-0 mt-4">
          <button
            className="btn btn-outline-secondary px-4 py-2 rounded-3"
            onClick={() => setWalletModal(false)}
          >
            Cancel
          </button>
          <button
            className="btn btn-success px-4 py-2 rounded-3 shadow-sm"
            onClick={() => handleWalletSubmit()}
          >
            Pay ${totalAmount?.toFixed(2)}
          </button>
        </div>
      </Modal>
    </>
  );
}

export default AddPromocode;